import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UnsplashCredit } from '../components/common/UnsplashCredit';
import { ArrowLeft, Share2 } from 'lucide-react';
import { BottomNav } from '../components/navigation';
import {
  DestinationDetail,
  DestinationPlansResponse,
  GenerationPayload,
  PlanCard,
  fetchDestination,
  fetchDestinationPlans,
  fetchGeneration,
  queuePendingSave,
  requestGeneration,
  saveDestination,
} from '../services/atlasApi';
import { useUserStore } from '../stores/useUserStore';
import { ApiError } from '../services/apiBase';
import { loginUrlReturningTo } from '../utils/returnTo';
import {
  trackDestinationSaved,
  trackDestinationShared,
  trackDestinationViewed,
} from '../hooks/useAnalytics';
import './DestinationPage.css';

/**
 * A destination page (spec §22.3) — the guide, readable with no account.
 *
 * `/d/:slug` is canonical and shows whatever edition is current. `/d/:slug/e/:n`
 * is PINNED and keeps rendering edition n after newer ones exist, because a
 * guide is a snapshot and a share URL whose content silently changes is one
 * nobody can cite (§22.1). A pinned page says so, and offers the newer one.
 */
const DestinationPage: React.FC = () => {
  const { slug, edition } = useParams<{ slug: string; edition?: string }>();
  const [searchParams] = useSearchParams();
  /** §1.8's filters, as they arrived from the map. `any` is the absence of a
   *  filter, not a filter — the server treats it that way and so must the
   *  headline, or an unfiltered tap would claim to be filtered. */
  const planFilters = useMemo(() => {
    const pick = (key: string) => {
      // Lowercased and trimmed, because `normalize` does exactly that on the
      // server: `?length=ANY` is no filter there, and a page that read it as
      // one would wear the chip and claim "7 of 7 plans match".
      const value = searchParams.get(key)?.trim().toLowerCase();
      return value && value !== 'any' ? value : undefined;
    };
    return { length: pick('length'), group: pick('group'), band: pick('band') };
  }, [searchParams]);
  const isFiltered = Boolean(planFilters.length || planFilters.group || planFilters.band);
  /** Identifies WHICH question a loaded list answers. The filters are part of
   *  it, because a list fetched under one filter does not describe another. */
  const plansKey = `${slug ?? ''}|${planFilters.length ?? ''}|${planFilters.group ?? ''}|${planFilters.band ?? ''}`;
  const location = useLocation();
  const navigate = useNavigate();
  const [detailState, setDetailState] = useState<{
    payload: DestinationDetail; route: string; userId: number | null;
  } | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [shareNote, setShareNote] = useState('');
  const [generationState, setGenerationState] = useState<{
    payload: GenerationPayload; route: string; userId: number;
  } | null>(null);
  const [generationStarting, setGenerationStarting] = useState(false);
  const [generationPollTick, setGenerationPollTick] = useState(0);
  const [generationRecoveryTick, setGenerationRecoveryTick] = useState(0);
  const [generationRecovery, setGenerationRecovery] = useState<
    'pending' | 'none' | 'error' | 'ready'>('pending');
  // Which guides have a save in flight, and which failed. SETS, not scalars:
  // a single key could only remember the most recent save, so starting one on
  // edition 1, another on edition 3, then returning to edition 1 showed the
  // first as un-started and let it be sent twice.
  //
  // Neither is ever the source of truth for "is this saved" — the server is.
  const [savingKeys, setSavingKeys] = useState<Set<string>>(() => new Set());
  const [errorKeys, setErrorKeys] = useState<Set<string>>(() => new Set());
  // The destination's community plans, fetched only when asked for: the count
  // arrives with the detail payload, and most readers never open the list.
  //
  // The rows, their load state and the slug they belong to are ONE value. Held
  // separately, the slug-keyed parts and the global parts drifted: a failure on
  // one destination stayed in `state` and hid another's already-loaded rows
  // behind an error, with no way back.
  const [plansLoad, setPlansLoad] = useState<
    {
      slug: string; state: 'loading' | 'idle' | 'failed'; rows: PlanCard[];
      // Keyed by slug AND filters. Keyed by slug alone, the cache answered a
      // different question than the one asked: clearing the filters removed
      // the chip and left the filtered rows on screen for good, because
      // reopening the panel short-circuited on "same slug, already idle".
      key: string;
      // Both numbers from the ONE response §1.8 requires them to share, so
      // "3 of 7" can never be assembled from two reads that disagree.
      count?: number; total?: number;
    } | null
  >(null);
  // Which slug's panel is open. Keyed, not a bare bool — the component survives
  // slug changes, so an open panel would otherwise carry over.
  const [plansOpenFor, setPlansOpenFor] = useState<string | null>(null);
  /** Which shortcut request is in flight, by ticket — NOT a component-wide
   *  boolean (r605 finding 2). Held globally, a hung request on destination A
   *  left B's control disabled forever, even when B has many plans. The ticket
   *  moves on every identity change, so scoping to it clears this by itself. */
  const [busyTicket, setBusyTicket] = useState<number | null>(null);
  /** SLICE D. Set only by Explore — see `destinationHref`. A shared link, a
   *  saved guide and the Plan tab's lens all reach this same address wanting
   *  the page they asked for. */
  const jumpsToSolePlan = searchParams.get('jump') === 'plan';
  /** Slice D is IN FLIGHT, so this page is not where the reader is going and
   *  must not be painted: rendering the guide and replacing it a moment later
   *  is the flash the shortcut exists to avoid.
   *
   *  A TICKET, not a boolean (r738 finding 2). A bare flag is cleared only by
   *  the request settling, and a request has no timeout — so a reader who
   *  left mid-jump and came back to a still-unresolved one found a spinner
   *  with nothing left to clear it. Held against the shortcut's own ticket,
   *  the hold expires exactly when the attempt it belongs to is retired:
   *  blur, an in-place move to another destination, anything that bumps it. */
  const [jumpTicket, setJumpTicket] = useState<number | null>(null);

  const plansInFlight = useRef<string | null>(null);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const userId = useUserStore((s) => s.user?.id ?? null);
  const currentUserId = useRef<number | null>(userId);
  currentUserId.current = userId;
  const generationRequestKey = useRef<{ route: string; userId: number; key: string } | null>(null);
  const generationStartingRef = useRef(false);
  const detailRequestEpoch = useRef(0);

  const pinned = edition === undefined ? undefined : Number(edition);
  const badPin = pinned !== undefined && !Number.isInteger(pinned);
  // The guide currently on screen. Every save-related piece of state is
  // compared against this, so nothing can survive a change of guide.
  const trackedView = useRef<string | null>(null);
  // Bumped to ask the server again — after a save, so the button reflects what
  // was actually written rather than what this component believes.
  const [readCount, setReadCount] = useState(0);
  // Which route the rendered guide belongs to. React Router keeps this
  // component mounted across `/d/a` → `/d/b`, so "we already have content" is
  // only a reason to keep it if the content is for THIS route: without the
  // scoping, a 404 for a missing slug was suppressed by the previous slug's
  // success and its guide stayed on screen under the wrong address.
  const loadedRoute = useRef<string | null>(null);
  const routeKey = `${slug ?? ''}:${pinned ?? 'current'}`;
  const currentRoute = useRef(routeKey);
  currentRoute.current = routeKey;
  const data = detailState?.route === routeKey && detailState.userId === userId
    ? detailState.payload : null;
  const installDetail = useCallback((payload: DestinationDetail) => {
    setDetailState({ payload, route: routeKey, userId });
  }, [routeKey, userId]);
  const generation = generationState?.route === routeKey
    && generationState.userId === userId ? generationState.payload : null;
  const installGeneration = useCallback((payload: GenerationPayload) => {
    if (userId !== null) setGenerationState({ payload, route: routeKey, userId });
  }, [routeKey, userId]);

  // Re-read when identity changes. The page can mount before auth resolves —
  // a shared link opens anonymously and the session settles a moment later —
  // and `is_saved` is answered per reader, so a fetch made while signed out
  // reports "not saved" for a guide the reader has in fact saved.
  useEffect(() => {
    if (!slug) return;
    if (badPin) { setStatus('missing'); return; }
    let cancelled = false;
    const requestEpoch = ++detailRequestEpoch.current;
    // A re-read of the SAME route keeps what is on screen until it has
    // something better — a spinner replacing a guide the reader is looking at
    // is a worse answer than a beat of staleness. A different route has
    // nothing to keep.
    const isReread = loadedRoute.current === routeKey;
    setStatus((current) => (isReread && current === 'ready' ? 'ready' : 'loading'));
    fetchDestination(slug, pinned)
      .then((found) => {
        if (cancelled || requestEpoch !== detailRequestEpoch.current) return;
        // The slug was RENAMED. Correct the address rather than showing this
        // place under a stale one — replace, so Back still leaves.
        //
        // Return before tracking: the corrected URL re-runs this effect and
        // records the view itself. Tracking first counted every renamed link
        // twice, which inflates exactly the number this funnel exists to read.
        if (found.redirected_from && found.slug !== slug) {
          // Keep the query string: ?from= is the attribution for the share
          // that just worked, and correcting the address must not cost it.
          const path = pinned === undefined ? `/d/${found.slug}` : `/d/${found.slug}/e/${pinned}`;
          navigate(`${path}${location.search}`, { replace: true });
          return;
        }
        installDetail(found);
        loadedRoute.current = routeKey;
        setStatus('ready');
        // Once per guide, not once per fetch. The identity-triggered re-read
        // would otherwise record a second view for the same reader looking at
        // the same page — inflating exactly the number this funnel exists to
        // report.
        const viewKey = `${found.slug}:${found.guide?.edition_number ?? 'none'}`;
        if (trackedView.current !== viewKey) {
          trackedView.current = viewKey;
          // `source` distinguishes a shared link from in-app browsing.
          trackDestinationViewed(
            found.slug,
            found.guide?.edition_number ?? null,
            searchParams.get('from') || (pinned !== undefined ? 'pinned_link' : 'direct'),
            found.is_current_edition,
          );
        }
      })
      .catch(() => {
        if (cancelled || requestEpoch !== detailRequestEpoch.current) return;
        // Only a re-read of THIS route can decline to conclude the guide is
        // missing. The confirming read after a save, or the identity re-read,
        // failing on transport must not replace a rendered guide with "we
        // don't have a guide for that place yet" — but a genuine 404 for a
        // slug we have never loaded must still say so.
        setStatus((current) => (isReread && current === 'ready' ? 'ready' : 'missing'));
      });
    return () => { cancelled = true; };
  }, [slug, pinned, badPin, routeKey, navigate, searchParams, location.search,
      isAuthenticated, userId, readCount, installDetail]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug, edition]);

  // Recover only THIS account's generation. The backend's no-id endpoint is
  // requester-scoped, and every later poll pins the returned id so a newer run
  // by another account can never replace the preview on screen.
  useEffect(() => {
    setGenerationState(null);
    setGenerationStarting(false);
    generationStartingRef.current = false;
    setGenerationRecovery('pending');
    if (!slug || pinned !== undefined || !isAuthenticated || userId === null) {
      setGenerationRecovery('none');
      return;
    }
    let current = true;
    let retryTimer: number | undefined;
    fetchGeneration(slug)
      .then((value) => {
        if (current && currentRoute.current === routeKey
            && currentUserId.current === userId) {
          installGeneration(value);
          setGenerationRecovery('ready');
        }
      })
      .catch((err) => {
        if (!current) return;
        if (err instanceof ApiError && err.status === 404) setGenerationRecovery('none');
        else {
          setGenerationRecovery('error');
          retryTimer = window.setTimeout(() => {
            if (current && currentRoute.current === routeKey
                && currentUserId.current === userId) {
              setGenerationRecoveryTick((n) => n + 1);
            }
          }, 1_500);
        }
      });
    return () => {
      current = false;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [slug, pinned, routeKey, isAuthenticated, userId, installGeneration,
      generationRecoveryTick]);

  useEffect(() => {
    if (!slug || !generation || pinned !== undefined || userId === null) return;
    const requestedRoute = routeKey;
    const requestedUser = userId;
    let current = true;
    let retryTimer: number | undefined;
    if (generation.status === 'published') {
      fetchDestination(slug)
        .then((found) => {
          if (!current || currentRoute.current !== requestedRoute
              || currentUserId.current !== requestedUser) return;
          if (found.guide?.edition_number !== generation.edition_number) {
            retryTimer = window.setTimeout(() => {
              if (current && currentRoute.current === requestedRoute
                  && currentUserId.current === requestedUser) {
                setGenerationPollTick((n) => n + 1);
              }
            }, 1_500);
            return;
          }
          detailRequestEpoch.current += 1;
          installDetail(found);
          const publishedEdition = found.guide!.edition_number;
          const viewKey = `${found.slug}:${publishedEdition}`;
          if (trackedView.current !== viewKey) {
            trackedView.current = viewKey;
            trackDestinationViewed(found.slug, publishedEdition,
              'direct', found.is_current_edition);
          }
          setGenerationState(null);
        })
        .catch(() => { retryTimer = window.setTimeout(() => {
          if (current && currentRoute.current === requestedRoute
              && currentUserId.current === requestedUser) {
            setGenerationPollTick((n) => n + 1);
          }
        }, 1_500); });
      return () => {
        current = false;
        if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      };
    }
    if (generation.status === 'failed') return;
    const delay = generation.status === 'held_for_review' ? 15_000 : 1_500;
    const timer = window.setTimeout(() => {
      fetchGeneration(slug, generation.generation_id)
        .then((value) => {
          if (!current || currentRoute.current !== requestedRoute
              || currentUserId.current !== requestedUser) return;
          // Keep the read-only preview across the publication handoff. The
          // canonical detail read above clears it only after the new edition is
          // confirmed, so the older edition never regains Save/Share briefly.
          installGeneration(value.status === 'published' ? {
            ...value, is_draft: generation.is_draft, guide: generation.guide,
          } : value);
        })
        .catch(() => {
          if (current) setGenerationPollTick((n) => n + 1);
        });
    }, delay);
    return () => { current = false; window.clearTimeout(timer); };
  }, [slug, pinned, userId, routeKey, generation, generationPollTick, installGeneration,
      installDetail]);

  const guideNumber = data?.guide?.edition_number ?? null;
  const guideKey = data ? `${data.slug}:${guideNumber ?? 'none'}` : null;
  // Derived, never stored: 'saving' only while THIS guide's POST is in flight,
  // 'saved' only when the server says so for THIS guide, 'error' only for the
  // attempt that failed on it.
  const saveState: 'idle' | 'saving' | 'saved' | 'error' =
    guideKey && savingKeys.has(guideKey) ? 'saving'
    : data?.is_saved ? 'saved'
    : guideKey && errorKeys.has(guideKey) ? 'error'
    : 'idle';

  /**
   * Reveal the destination's community plans (§22.7's Explore↔Plan bridge).
   *
   * Fetched by SLUG through the destination FK, which is what `plan_count`
   * counts, so the list length is the number on the button. The old CTA passed a
   * NAME to `/explore?destination=`, which `PlansV2` never read — it promised a
   * filter and delivered the unfiltered feed.
   */
  const loadPlans = useCallback(async (): Promise<DestinationPlansResponse | null> => {
    if (!data) return null;
    const slug = data.slug;
    // One press per destination in flight. Two fast clicks share a stale
    // closure and would otherwise issue duplicate GETs.
    if (plansInFlight.current === plansKey) return null;
    plansInFlight.current = plansKey;
    setPlansLoad({ slug, key: plansKey, state: 'loading', rows: [] });
    try {
      const res = await fetchDestinationPlans(slug, planFilters);
      // The reader may have moved on mid-request; painting now would show one
      // destination's plans under another's name.
      if (loadedRoute.current !== routeKey) return null;
      setPlansLoad({
        slug, key: plansKey, state: 'idle', rows: res.results,
        count: res.count, total: res.total,
      });
      return res;
    } catch {
      // Guarded like the success path: a failure from a route the reader has
      // left must not describe the one they are on.
      if (loadedRoute.current !== routeKey) return null;
      setPlansLoad({ slug, key: plansKey, state: 'failed', rows: [] });
      return null;
    } finally {
      if (plansInFlight.current === plansKey) plansInFlight.current = null;
    }
  }, [data, routeKey, planFilters, plansKey]);

  /** The CTA: open, or close again. Retry does NOT come through here.
   *
   * THE ONE-PLAN SHORTCUT (Sep-7 field round, issue 4). A panel that lists a
   * single item is pure overhead, and production says the atlas is ~1 plan
   * per place — the common case, not the corner.
   *
   * The decision is made on the ROWS, never on `plan_count`: that count came
   * with the page and a plan can be unpublished under it, while `total` is
   * the unfiltered count carried by the SAME response as the rows, so the two
   * cannot disagree. `plan_count` only chooses whether to defer opening the
   * panel — every other destination behaves exactly as before. A destination
   * that turns out to hold more, or none, or that fails to load, falls back
   * to the panel, which is also where the retry lives.
   *
   * Only ever from an explicit click: the filtered auto-open effect below
   * must not navigate on its own. */
  /* THE SHORTCUT'S INTENT TICKET (r604 finding 1).
   *
   * The load guards below protect PAINTING — they decide whether a response
   * may fill the panel. Reusing their success as authority to NAVIGATE was
   * wrong: `loadedRoute` still names destination A until B's detail request
   * lands, so A's plans response arriving in that gap would move a reader who
   * is already on their way to B.
   *
   * The shortcut therefore carries its own ticket, and it is MONOTONIC rather
   * than an equality check against the current key: A -> B -> A returns to the
   * same key, and an equality check cannot tell the two A's apart.
   *
   * Bumped during RENDER rather than in an effect, because an effect runs
   * after paint and a response can land in between. */
  const shortcutTicket = useRef(0);
  const shortcutIdentity = `${routeKey}|${plansKey}`;
  const lastShortcutIdentity = useRef(shortcutIdentity);
  /** One navigation per arrival (r604 finding 2). The cached branch never
   *  awaits, so neither the busy flag nor `loadPlans`' in-flight guard can
   *  stop a second press pushing the same screen twice. */
  const shortcutNavigated = useRef(false);
  /** Slice D has attempted this arrival. `shortcutNavigated` cannot serve: it
   *  latches only on a navigation, so an arrival finding TWO plans would
   *  re-run on every render — and `runShortcut` is a dependency of the effect,
   *  so a fallback that writes `plansLoad` changes it and re-runs it. */
  const autoJumpTried = useRef(false);
  if (lastShortcutIdentity.current !== shortcutIdentity) {
    lastShortcutIdentity.current = shortcutIdentity;
    shortcutTicket.current += 1;
    shortcutNavigated.current = false;
    autoJumpTried.current = false;
  }
  const shortcutBusy = busyTicket !== null && busyTicket === shortcutTicket.current;
  /** Slice D's hold, alive only while the arrival that opened it is. */
  const jumping = jumpTicket !== null && jumpTicket === shortcutTicket.current;

  /* LEAVING retires anything in flight (r605 finding 1). The bump above needs
   * a RENDER, and a reader who presses and then leaves — Back, another route,
   * another tab — can unmount without one, leaving the closure holding a
   * ticket that still matches.  */
  useEffect(() => () => { shortcutTicket.current += 1; }, []);

  /** The one-plan shortcut, shared by the CTA and by slice D's arrival.
   *
   *  `how` is the only difference. A press came FROM this page, so the plan is
   *  pushed and Back returns here. An arrival was never meant to stop here, so
   *  the plan REPLACES it in history and Back returns to Explore — leaving a
   *  page the reader never asked for in the stack is the same overhead slice D
   *  removes, moved to the way out. */
  const runShortcut = useCallback(async (
    dest: DestinationDetail, how: 'push' | 'replace',
    onTicket?: (ticket: number) => void,
  ) => {
    if (shortcutNavigated.current) return;
    const mine = ++shortcutTicket.current;
    onTicket?.(mine);
    const cached = plansLoad?.key === plansKey && plansLoad.state === 'idle'
      ? plansLoad : null;
    let res: { results: PlanCard[]; total?: number } | null;
    if (cached) {
      res = { results: cached.rows, total: cached.total };
    } else {
      setBusyTicket(mine);
      try {
        res = await loadPlans();
      } finally {
        // Only MY request may clear the flag: a successor's is not mine
        // to retire (r606 finding 1).
        setBusyTicket((t) => (t === mine ? null : t));
      }
    }
    // The reader's question may have changed while this was in flight.
    // This governs the panel FALLBACK too: opening a panel for a
    // destination they have left strands state for their return.
    if (shortcutTicket.current !== mine) return;
    if (res && res.results.length === 1 && res.total === 1) {
      shortcutNavigated.current = true;
      const to = `/plans/p/${res.results[0].slug}`;
      if (how === 'replace') navigate(to, { replace: true }); else navigate(to);
      return;
    }
    setPlansOpenFor(dest.slug);
  }, [plansLoad, plansKey, loadPlans, navigate]);

  const togglePlans = useCallback(async () => {
    if (!data) return;
    if (plansOpenFor === data.slug) { setPlansOpenFor(null); return; }

    if (data.plan_count === 1 && !shortcutBusy) {
      await runShortcut(data, 'push');
      return;
    }

    setPlansOpenFor(data.slug);
    // Already loaded THIS destination: reopening is free. The load carries its
    // own slug, so a previous destination's failure cannot describe this one.
    if (plansLoad?.key === plansKey && plansLoad.state === 'idle') return;
    void loadPlans();
  }, [data, plansOpenFor, plansLoad, loadPlans, plansKey, runShortcut, shortcutBusy]);

  /* SLICE D — the arrival that does not stop here.
   *
   * A destination on Explore opened this page, and #267 then asked for one
   * more press to reach the sole plan. Three field reports pointed at that
   * press (Sep-7 issues 2 and 4, Sep-10 issue 3); this removes it.
   *
   * It runs the SAME shortcut the CTA runs, so the rule lives in one place:
   * the decision is made on the ROWS — `results.length === 1 && total === 1`
   * — never on `plan_count`, which only decides whether it is worth asking. A
   * destination holding more, none, or one that fails to load falls through to
   * the ordinary page, and so does a FILTERED arrival whose one row is one of
   * seven: `total` is the unfiltered count carried by the same response, so
   * the "1 of 7" receipt survives rather than being jumped past. */
  /* A LAYOUT effect, which is the whole reason this page does not flash.
   * `useEffect` runs after the commit is painted, so the guide would appear
   * for a frame before being replaced — exactly what the shortcut is for.
   * `useLayoutEffect` runs before paint, so the spinner is the first and only
   * thing drawn. A render-time predicate stood here doing the same job by
   * hand; the hook is the mechanism React already has for it. */
  useLayoutEffect(() => {
    if (!jumpsToSolePlan || autoJumpTried.current) return;
    if (!data) return;
    if (data.plan_count !== 1) return;
    autoJumpTried.current = true;
    let mine: number | null = null;
    void runShortcut(data, 'replace', (t) => { mine = t; setJumpTicket(t); })
      // Only MY hold is mine to retire; a successor's is not.
      .finally(() => setJumpTicket((t) => (t === mine ? null : t)));
  }, [jumpsToSolePlan, data, runShortcut]);

  /** §1.8 asks that the page a reader LANDS on shows the matching plans under a
   *  visible chip. The panel was closed until pressed, so a reader who tapped a
   *  pin numbered 3 arrived at "7 plans for Granada" with no chip at all and had
   *  to press before the promise was kept — the pin's number and the page
   *  disagreeing on arrival, which is precisely the case this section says must
   *  hold "in both states rather than only the default".
   *  It also refetches when the filters change under an OPEN panel, which is
   *  what makes Clear work: nothing observed the address before, so clearing
   *  removed the chip and left the filtered rows in place permanently. */
  useEffect(() => {
    if (!data) return;
    if (isFiltered && plansOpenFor !== data.slug) { setPlansOpenFor(data.slug); return; }
    if (plansOpenFor !== data.slug) return;
    if (plansInFlight.current === plansKey) return;
    if (plansLoad?.key === plansKey) return;
    void loadPlans();
  }, [data, isFiltered, plansOpenFor, plansKey, plansLoad, loadPlans]);

  const share = useCallback(async () => {
    if (!data) return;
    // Share the PINNED url, always. Sending the canonical one means the
    // recipient may read something the sender never saw.
    const url = `${window.location.origin}/d/${data.slug}`
      + (guideNumber !== null ? `/e/${guideNumber}` : '');
    const title = data.where;
    const nav = window.navigator as Navigator & {
      share?: (d: { title: string; url: string }) => Promise<void>;
    };
    try {
      if (nav.share) {
        await nav.share({ title, url });
        trackDestinationShared(data.slug, guideNumber, 'web_share');
      } else {
        await window.navigator.clipboard.writeText(url);
        trackDestinationShared(data.slug, guideNumber, 'clipboard');
        setShareNote('Link copied');
        window.setTimeout(() => setShareNote(''), 2000);
      }
    } catch {
      // A dismissed share sheet is not a failure and not a share.
    }
  }, [data, guideNumber]);

  const save = useCallback(async () => {
    if (!data || !guideKey || saveState === 'saving' || saveState === 'saved') return;
    // Pin to the edition on screen, so what gets kept is what was read.
    const edition = data.guide?.edition_number;
    if (!isAuthenticated) {
      // Queue and send them to sign up. Dates are NOT asked for here — §22.3
      // is explicit that dates, signup and the who-picker never precede
      // seeing the trip, and this is the "seeing" half.
      queuePendingSave(data.slug, edition ?? null);
      // Recorded at the point of INTENT, not only on success: a save that
      // routes through signup is the funnel step worth knowing about, and
      // counting it only after the replay hides everyone who drops at the gate.
      trackDestinationSaved(data.slug, edition ?? null, 'signed_out');
      // The ROUTER's location, not window.location: the router owns the path
      // inside a SPA, and reading the global one couples this to how the app
      // happens to be mounted.
      navigate(loginUrlReturningTo(location.pathname + location.search));
      return;
    }
    setSavingKeys((keys) => new Set(keys).add(guideKey));
    setErrorKeys((keys) => {
      if (!keys.has(guideKey)) return keys;
      const next = new Set(keys);
      next.delete(guideKey);
      return next;
    });
    try {
      await saveDestination(data.slug, edition);
      trackDestinationSaved(data.slug, edition ?? null, 'destination_page');
      // Confirmed by RE-READING, not by patching local state. A GET already in
      // flight when the POST resolved would otherwise install its older
      // `is_saved: false` afterwards and erase a save that had succeeded.
      // Asking again is ordered after the write, so it is the last word.
      setReadCount((n) => n + 1);
    } catch {
      setErrorKeys((keys) => new Set(keys).add(guideKey));
    } finally {
      setSavingKeys((keys) => {
        if (!keys.has(guideKey)) return keys;
        const next = new Set(keys);
        next.delete(guideKey);
        return next;
      });
    }
  }, [data, guideKey, saveState, isAuthenticated, navigate, location]);

  const startGeneration = useCallback(async () => {
    if (!data || generationStartingRef.current) return;
    if (!isAuthenticated) {
      navigate(loginUrlReturningTo(location.pathname + location.search));
      return;
    }
    generationStartingRef.current = true;
    setGenerationStarting(true);
    const requestedRoute = routeKey;
    const requestedUser = userId;
    if (generation?.status === 'failed' && generation.generation_id > 0) {
      generationRequestKey.current = null;
    }
    try {
      if (!generationRequestKey.current
          || generationRequestKey.current.route !== requestedRoute
          || generationRequestKey.current.userId !== requestedUser) {
        generationRequestKey.current = {
          route: requestedRoute, userId: requestedUser!,
          key: `web:${Date.now()}:${Math.random().toString(36).slice(2, 14)}`,
        };
      }
      const key = generationRequestKey.current.key;
      const value = await requestGeneration(data.slug, key);
      if (loadedRoute.current === requestedRoute
          && currentUserId.current === requestedUser) {
        installGeneration(value);
      }
    } catch {
      if (loadedRoute.current === requestedRoute
          && currentUserId.current === requestedUser) installGeneration({
        generation_id: 0, destination_slug: data.slug, status: 'failed',
        can_save: false, is_draft: false,
        failure_reason: 'That guide could not be started.',
      });
    } finally {
      if (loadedRoute.current === requestedRoute
          && currentUserId.current === requestedUser) {
        generationStartingRef.current = false;
        setGenerationStarting(false);
      }
    }
  }, [data, isAuthenticated, navigate, location, routeKey, userId,
      installGeneration, generation]);

  if (jumping || status === 'loading') {
    return (
      <div className="dp-page">
        <div className="dp-state">Loading…</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  if (status === 'missing' || !data) {
    return (
      <div className="dp-page">
        <div className="dp-state">
          <p>We don’t have a guide for that place yet.</p>
          <button className="dp-back-link" onClick={() => navigate('/explore')}>
            Browse destinations
          </button>
        </div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  const where = data.where;
  const draftGuide = generation?.is_draft ? generation.guide : undefined;
  const guide = draftGuide ?? data.guide;
  const showingDraft = Boolean(draftGuide);
  const generationLocksActions = Boolean(generation && generation.status !== 'failed');

  return (
    <div className="dp-page">
      <header
        className={`dp-hero${data.hero_url ? '' : ' dp-hero--bare'}`}
        style={data.hero_url ? { backgroundImage: `url(${data.hero_url})` } : undefined}
      >
        <button className="dp-back" onClick={() => navigate(-1)} aria-label="Go back">
          <ArrowLeft size={20} />
        </button>
        {!generationLocksActions && data.guide && (
          <button className="dp-share" onClick={share} aria-label="Share this guide">
            <Share2 size={18} />
          </button>
        )}
        {/* Inside the hero, over the scrim, because that is the image it
            credits — and outside the back/share buttons' hit areas. */}
        {data.hero_url && data.hero_attribution && (
          <UnsplashCredit
            photographer={data.hero_attribution.photographer}
            profileLink={data.hero_attribution.unsplash_link}
            className="dp-hero-credit"
          />
        )}
        <div className="dp-hero-text">
          <h1>{data.name}</h1>
          {/* The qualifier, not the country: "MA" under "Chatham".
              One rule on the model, so this line and the share sheet cannot
              disagree about which Chatham this is (r763). */}
          {data.where_sub && <p className="dp-country">{data.where_sub}</p>}
        </div>
      </header>

      {shareNote && <p className="dp-share-note" role="status">{shareNote}</p>}

      <div className="dp-body">
        {data.merged_into && (
          <div className="dp-superseded">
            <p>
              {data.name} is now part of {data.merged_into.name}. You’re reading
              the guide as it was.
            </p>
            <button onClick={() => navigate(`/d/${data.merged_into!.slug}`)}>
              Go to {data.merged_into.name}
            </button>
          </div>
        )}

        {!data.is_current_edition && data.current_edition_number !== null && (
          <div className="dp-superseded">
            <p>
              You’re reading edition <span className="u-timetable">{guideNumber}</span>.
              Edition <span className="u-timetable">{data.current_edition_number}</span> is out.
            </p>
            <button onClick={() => navigate(`/d/${data.slug}`)}>Read the current edition</button>
          </div>
        )}

        {guide ? (
          <>
            <div className="dp-guide-head">
              <h2>{guide.title}</h2>
              <p className="dp-meta u-timetable">
                {guide.duration_days} days
                {showingDraft ? ' · preview' : ` · edition ${data.guide!.edition_number}`}
              </p>
            </div>

            <ol className="dp-days">
              {guide.days.map((day, i) => (
                <li className="dp-day" key={day.day ?? i}>
                  <div className="dp-day-rail">
                    <span className="dp-day-num u-timetable">
                      {String(day.day ?? i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="dp-day-body">
                    {day.label && <h3>{day.label}</h3>}
                    <ul className="dp-activities">
                      {(day.activities || []).map((act, j) => (
                        <li key={j}>
                          {act.time && <span className="dp-time u-timetable">{act.time}</span>}
                          <span className="dp-act-name">{act.name}</span>
                          {act.location && <span className="dp-act-where">{act.location}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <div className="dp-state">
            {generation && generation.status !== 'failed' ? (
              <>
                <p>
                  {generation.status === 'held_for_review'
                    ? 'Your guide is awaiting review.'
                    : `Building your guide for ${where}…`}
                </p>
                <p>Keep this page open — the preview will appear here.</p>
              </>
            ) : generationRecovery === 'none' || generation?.status === 'failed' ? (
              <>
                <p>There isn’t a guide for {where} yet.</p>
                <button className="dp-save" onClick={startGeneration}
                  disabled={generationStarting}>
                  {generationStarting ? 'Starting…' : 'Build one with AI'}
                </button>
                {generation?.status === 'failed' && (
                  <p role="status">{generation.failure_reason || 'That didn’t work. Try again.'}</p>
                )}
              </>
            ) : <p>{generationRecovery === 'error'
              ? 'Checking for your existing guide…' : 'Checking for an existing guide…'}</p>}
          </div>
        )}

        {generationLocksActions && (
          <div className="dp-actions">
            <p role="status">Preview only — save and share unlock after publication.</p>
            {generation?.status === 'held_for_review' && (
              <p>It will refresh here after an owner approves it.</p>
            )}
          </div>
        )}

        {guide && !generationLocksActions && (
          <div className="dp-actions">
            <button
              className="dp-save"
              onClick={save}
              disabled={saveState === 'saving' || saveState === 'saved'}
            >
              {saveState === 'saved' ? 'Saved to your plans'
                : saveState === 'saving' ? 'Saving…'
                : 'Save this plan'}
            </button>
            {saveState === 'saved' && (
              <button className="dp-secondary" onClick={() => navigate('/plan')}>
                Go to your plans
              </button>
            )}
            {saveState === 'error' && (
              <p className="dp-save-error" role="status">
                That didn’t save. Try again.
              </p>
            )}
          </div>
        )}

        {data.plan_count > 0 && (
          <>
            <button
              className="dp-more"
              onClick={togglePlans}
              disabled={shortcutBusy}
              aria-busy={shortcutBusy}
              aria-expanded={plansOpenFor === data.slug}
              aria-controls="dp-plans"
            >
              {shortcutBusy ? 'Opening…' : (
                <>
                  <span className="u-timetable">{data.plan_count}</span>
                  {data.plan_count === 1 ? ' plan' : ' plans'} for {data.name}
                </>
              )}
            </button>

            {plansOpenFor === data.slug && (
              <div id="dp-plans" className="dp-plans">
                {/* §1.8: the matching plans "under a visible 'filtered — clear'
                    chip", with a headline reading "3 of 7 plans match". Both
                    numbers come from the one response, so the fraction cannot
                    be assembled from two reads that disagree — which is the
                    whole point of the section: "the pin's number and the page a
                    reader lands on never disagree".
                    Clearing navigates rather than just refetching, because the
                    filters live in the address: a cleared page that still
                    carried them would re-filter on any reload or share. */}
                {isFiltered && (
                  <div className="dp-plans-filter">
                    <p className="dp-plans-headline" role="status">
                      {plansLoad?.key === plansKey && plansLoad.state === 'idle'
                        && plansLoad.count !== undefined && plansLoad.total !== undefined
                        ? `${plansLoad.count} of ${plansLoad.total} `
                          + `${plansLoad.total === 1 ? 'plan' : 'plans'} match`
                        : 'Filtered from the map'}
                    </p>
                    <button
                      className="dp-plans-clear"
                      onClick={() => navigate(`/d/${data.slug}`, { replace: true })}
                    >
                      Clear
                    </button>
                  </div>
                )}
                {/* A load belonging to another slug is not this panel's state:
                    until this destination's own load lands, this is loading. */}
                {(plansLoad?.key !== plansKey || plansLoad.state === 'loading') && (
                  <p className="dp-plans-state" role="status">Loading those plans…</p>
                )}
                {plansLoad?.key === plansKey && plansLoad.state === 'failed' && (
                  // Retry refetches; it must NOT go through the CTA, whose first
                  // act is to close the panel it is rendered inside.
                  <button className="dp-plans-retry" onClick={loadPlans}>
                    Couldn’t load those plans. Try again.
                  </button>
                )}
                {plansLoad?.slug === data.slug && plansLoad.state === 'idle'
                  && plansLoad.rows.map((p) => (
                  <button
                    key={p.id}
                    className="dp-plan-row"
                    onClick={() => navigate(`/plans/p/${p.slug}`)}
                  >
                    <span className="dp-plan-title">{p.title}</span>
                    <span className="dp-plan-meta u-timetable">
                      {p.duration_days} days{p.creator_name ? ` · ${p.creator_name}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default DestinationPage;
